// LUMINA · pages/AdminPage.jsx
// Admin dashboard — user management, tier upgrades, stats, teachings.

import React, { useState, useEffect } from 'react';
import { C, IS, LS, BG, BO } from '../constants/baziData';
import AdminOracleStats from './AdminOracleStats';

const API = "/api";

const TEACHING_TYPES = ["teaching","empowerment","retreat","practice","text_study"];
const TYPE_LABELS = { teaching:"法教", empowerment:"灌顶", retreat:"闭关", practice:"实修", text_study:"经论" };

export default function AdminPage({user}){
  const [stats,setStats]=useState(null);
  const [users,setUsers]=useState([]);
  const [total,setTotal]=useState(0);
  const [page,setPage]=useState(1);
  const [search,setSearch]=useState("");
  const [filterTier,setFilterTier]=useState("");
  const [editUser,setEditUser]=useState(null);
  const [newTier,setNewTier]=useState("");
  const [reason,setReason]=useState("");
  const [resetPw,setResetPw]=useState("");
  const [msg,setMsg]=useState("");
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const token=localStorage.getItem("lumina_token");
  const headers={"Authorization":`Bearer ${token}`,"Content-Type":"application/json"};
  const [adminTab,setAdminTab]=useState("users");

  // ── Teaching state ──
  const [vajraToken,setVajraToken]=useState(localStorage.getItem("vajra_token")||"");
  const [teachings,setTeachings]=useState([]);
  const [tLoading,setTLoading]=useState(false);
  const [tForm,setTForm]=useState(null);       // null=closed, {} = create, {id}=edit
  const [tFormData,setTFormData]=useState({});
  const [tDel,setTDel]=useState(null);          // delete confirm

  const vHeaders = vajraToken ? {"Authorization":`Bearer ${vajraToken}`,"Content-Type":"application/json"} : null;

  const loadStats=async()=>{
    try{const r=await fetch(`${API}/admin/dashboard`,{headers});if(r.ok){setStats(await r.json());}}catch(e){}
  };

  const loadUsers=async(p=1)=>{
    try{
      let url=`${API}/admin/users?page=${p}&per_page=15`;
      if(filterTier)url+=`&tier=${filterTier}`;
      if(search)url+=`&search=${encodeURIComponent(search)}`;
      const r=await fetch(url,{headers});
      if(r.ok){const d=await r.json();setUsers(d.users);setTotal(d.total);setPage(p);}
      else if(r.status===403){setMsg("Admin access required");}
    }catch(e){setMsg(e.message);}
  };

  const quickPro=async(userId,email)=>{
    try{
      const r=await fetch(`${API}/admin/users/${userId}/tier`,{
        method:"PUT",headers,body:JSON.stringify({tier:"pro",reason:"Admin: 开通澄光日课"})
      });
      if(r.ok){setMsg(`${email} → Pro (澄光日课已开通)`);loadUsers(page);loadStats();}
      else{const d=await r.json();setMsg(d.detail||"Error");}
    }catch(e){setMsg(e.message);}
  };

  const upgradeTier=async(userId)=>{
    if(!newTier)return;
    try{
      const r=await fetch(`${API}/admin/users/${userId}/tier`,{
        method:"PUT",headers,body:JSON.stringify({tier:newTier,reason})
      });
      const d=await r.json();
      if(r.ok){setMsg(d.message);setEditUser(null);setNewTier("");setReason("");loadUsers(page);loadStats();}
      else setMsg(d.detail||"Error");
    }catch(e){setMsg(e.message);}
  };

  const resetPassword=async(userId)=>{
    if(!resetPw||resetPw.length<8){setMsg("Password must be at least 8 characters");return;}
    try{
      const r=await fetch(`${API}/admin/users/${userId}/reset-password`,{
        method:"PUT",headers,body:JSON.stringify({new_password:resetPw})
      });
      const d=await r.json();
      if(r.ok){setMsg(d.message);setResetPw("");}
      else setMsg(d.detail||"Error");
    }catch(e){setMsg(e.message);}
  };

  const toggleActive=async(u)=>{
    try{
      const r=await fetch(`${API}/admin/users/${u.id}`,{
        method:"PUT",headers,body:JSON.stringify({is_active:!u.is_active})
      });
      if(r.ok){loadUsers(page);}
    }catch(e){}
  };

  const deleteUser=async(u)=>{
    try{
      const r=await fetch(`${API}/admin/users/${u.id}`,{
        method:"DELETE",headers
      });
      const d=await r.json();
      if(r.ok){setMsg(d.message);setDeleteConfirm(null);loadUsers(page);loadStats();}
      else setMsg(d.detail||"Error");
    }catch(e){setMsg(e.message);}
  };

  useEffect(()=>{loadStats();loadUsers();},[]);

  const bridgeToVajra=async()=>{
    try{
      const r=await fetch(`${API}/admin/bridge-vajra`,{headers});
      if(r.ok){
        const d=await r.json();
        localStorage.setItem("vajra_token",d.token);
        window.location.href="/vajra/admin";
      }else{
        window.open("/vajra/admin","_blank");
      }
    }catch(e){
      window.open("/vajra/admin","_blank");
    }
  };

  // ── Teaching: bridge to get vajra token for API calls (no redirect) ──
  const ensureVajraToken=async()=>{
    if(vajraToken) return true;
    try{
      const r=await fetch(`${API}/admin/bridge-vajra`,{headers});
      if(r.ok){
        const d=await r.json();
        localStorage.setItem("vajra_token",d.token);
        setVajraToken(d.token);
        return true;
      }
    }catch(e){}
    setMsg("需要 Vajra Staff 授权 — 请先点 'Vajra Staff →' 登录");
    return false;
  };

  const loadTeachings=async()=>{
    const ok=await ensureVajraToken();
    if(!ok) return;
    setTLoading(true);
    const vt = vajraToken || localStorage.getItem("vajra_token");
    const h = {"Authorization":`Bearer ${vt}`,"Content-Type":"application/json"};
    try{
      const r=await fetch(`${API}/staff/teachings`,{headers:h});
      if(r.ok) setTeachings(await r.json());
      else setMsg("加载教法失败");
    }catch(e){setMsg(e.message);}
    setTLoading(false);
  };

  useEffect(()=>{
    if(adminTab==="teachings") loadTeachings();
  },[adminTab,vajraToken]);

  const openCreateTeaching=()=>{
    setTFormData({
      title_zh:"", title_en:"", teaching_type:"teaching",
      teacher_id:"", tradition:"", lineage:"", description_zh:"",
      is_public:true, topic_hub_slug:"", credential_key:"",
    });
    setTForm({});
  };

  const openEditTeaching=(t)=>{
    setTFormData({
      title_zh:t.title_zh||"", title_en:t.title_en||"",
      teaching_type:t.teaching_type||"teaching",
      teacher_id:t.teacher_id||"", tradition:t.tradition||"",
      lineage:t.lineage||"", description_zh:t.description_zh||"",
      is_public:t.is_public!==false, topic_hub_slug:t.topic_hub_slug||"",
      credential_key:t.credential_key||"",
    });
    setTForm({id:t.id});
  };

  const saveTeaching=async()=>{
    if(!tFormData.title_zh.trim()){setMsg("标题不能为空");return;}
    const vt = vajraToken || localStorage.getItem("vajra_token");
    const h = {"Authorization":`Bearer ${vt}`,"Content-Type":"application/json"};
    const body = {
      title_zh: tFormData.title_zh,
      title_en: tFormData.title_en||null,
      teaching_type: tFormData.teaching_type||"teaching",
      teacher_id: tFormData.teacher_id ? parseInt(tFormData.teacher_id) : null,
      tradition: tFormData.tradition||null,
      lineage: tFormData.lineage||null,
      description_zh: tFormData.description_zh||null,
      is_public: tFormData.is_public,
      topic_hub_slug: tFormData.topic_hub_slug||null,
      credential_key: tFormData.credential_key||null,
    };
    try{
      const url = tForm.id
        ? `${API}/staff/teachings/${tForm.id}`
        : `${API}/staff/teachings`;
      const method = tForm.id ? "PUT" : "POST";
      const r=await fetch(url,{method,headers:h,body:JSON.stringify(body)});
      if(r.ok){
        setMsg(tForm.id?"教法已更新":"教法已创建");
        setTForm(null); setTFormData({}); loadTeachings();
      }else{
        const d=await r.json();
        setMsg(d.detail||"保存失败");
      }
    }catch(e){setMsg(e.message);}
  };

  const deleteTeaching=async()=>{
    const vt = vajraToken || localStorage.getItem("vajra_token");
    const h = {"Authorization":`Bearer ${vt}`,"Content-Type":"application/json"};
    try{
      const r=await fetch(`${API}/staff/teachings/${tDel.id}`,{method:"DELETE",headers:h});
      if(r.ok){
        setMsg(`已删除: ${tDel.title_zh}`);
        setTDel(null); loadTeachings();
      }else{
        const d=await r.json();
        setMsg(d.detail||"删除失败");
      }
    }catch(e){setMsg(e.message);}
  };

  if(msg==="Admin access required")return(
    <div style={{maxWidth:600,margin:"80px auto",textAlign:"center",padding:"0 20px"}}>
      <div style={{background:C.panel,border:`0.5px solid ${C.stress}`,borderRadius:14,padding:40}}>
        <div style={{fontSize:20,color:C.stress,fontFamily:"'Inter','Montserrat',sans-serif",marginBottom:12}}>Access Denied</div>
        <div style={{fontSize:15,color:C.mist3}}>Admin privileges required. Contact the system administrator.</div>
      </div>
    </div>
  );

  return(
    <div style={{maxWidth:1100,margin:"0 auto",padding:"40px 20px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:24,color:C.gold,fontFamily:"'Inter','Montserrat',sans-serif",letterSpacing:2}}>Admin Dashboard</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button style={{fontSize:12,color:"#D4AF37",background:"transparent",border:"1px solid rgba(212,175,55,0.3)",padding:"5px 14px",borderRadius:6,cursor:"pointer"}}
            onClick={bridgeToVajra}>Vajra Staff →</button>
        </div>
      </div>
      {/* Tab 切换 */}
      <div style={{display:"flex",gap:8,marginBottom:24}}>
        {[
          {key:"users", label:"👤 Users"},
          {key:"oracle", label:"🔮 Oracle"},
          {key:"teachings", label:"📚 教法"},
        ].map(t=>(
          <button key={t.key} onClick={()=>setAdminTab(t.key)} style={{
            background:adminTab===t.key?"rgba(212,175,55,.18)":"transparent",
            border:`1px solid ${adminTab===t.key?"rgba(212,175,55,.5)":"rgba(212,175,55,.2)"}`,
            color:adminTab===t.key?C.gold:C.mist3,
            borderRadius:6, padding:"6px 18px", fontSize:13, cursor:"pointer", fontWeight:adminTab===t.key?600:400,
          }}>{t.label}</button>
        ))}
      </div>
      {adminTab==="oracle"&&<AdminOracleStats />}
      {adminTab==="teachings"&&<span>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:14,color:C.mist3}}>
            {vajraToken?"":"需要 Vajra Staff 权限 — "}
            {!vajraToken && <button onClick={()=>ensureVajraToken().then(ok=>ok&&loadTeachings())}
              style={{background:"transparent",border:`0.5px solid ${C.cyan}`,borderRadius:4,padding:"3px 10px",fontSize:12,color:C.cyan,cursor:"pointer"}}>授权</button>}
          </div>
          <button onClick={openCreateTeaching} style={{
            background:C.gold,border:"none",borderRadius:8,padding:"10px 20px",color:"#fff",fontSize:14,fontWeight:600,cursor:"pointer"
          }}>+ 新建教法</button>
        </div>

        {msg&&<div style={{fontSize:13,color:C.sage,marginBottom:12}}>{msg}</div>}

        {tLoading ? (
          <div style={{textAlign:"center",padding:40,color:C.mist4}}>Loading...</div>
        ) : teachings.length===0 ? (
          <div style={{textAlign:"center",padding:40,color:C.mist4,fontSize:14}}>暂无教法，点击上方按钮创建</div>
        ) : (
          <div style={{background:"rgba(255,255,255,.72)",border:"1px solid rgba(212,175,55,.16)",backdropFilter:"blur(12px)",borderRadius:10,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"2fr 0.6fr 0.8fr 0.8fr 0.6fr 0.8fr",gap:0,padding:"10px 16px",background:C.panel2,fontSize:11,letterSpacing:1.5,color:C.mist4,textTransform:"uppercase"}}>
              <div>Title</div><div>Type</div><div>Teacher</div><div>Tradition</div><div>Active</div><div>Actions</div>
            </div>
            {teachings.map(t=>(
              <div key={t.id} style={{display:"grid",gridTemplateColumns:"2fr 0.6fr 0.8fr 0.8fr 0.6fr 0.8fr",gap:0,padding:"10px 16px",borderTop:`0.5px solid ${C.border}`,fontSize:13,alignItems:"center"}}>
                <div style={{color:C.mist,overflow:"hidden",textOverflow:"ellipsis"}}>
                  {t.title_zh}
                  {t.stages?.length>0&&<span style={{fontSize:10,color:C.mist4,marginLeft:6}}>({t.stages.length}阶段)</span>}
                </div>
                <div><span style={{fontSize:11,color:C.gold,background:C.goldGlow,borderRadius:4,padding:"1px 8px"}}>{TYPE_LABELS[t.teaching_type]||t.teaching_type}</span></div>
                <div style={{color:C.mist3}}>{t.teacher_name||"—"}</div>
                <div style={{color:C.mist4,fontSize:12}}>{t.tradition||"—"}</div>
                <div><span style={{color:t.is_active?C.sage:C.stress,fontSize:12}}>{t.is_active?"Active":"Inactive"}</span></div>
                <div style={{display:"flex",gap:6}}>
                  <button style={{background:"transparent",border:`0.5px solid ${C.cyan}`,borderRadius:4,padding:"3px 10px",fontSize:11,color:C.cyan,cursor:"pointer"}}
                    onClick={()=>openEditTeaching(t)}>Edit</button>
                  <button style={{background:"transparent",border:`0.5px solid ${C.stress}`,borderRadius:4,padding:"3px 10px",fontSize:11,color:C.stress,cursor:"pointer"}}
                    onClick={()=>setTDel(t)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Teaching Form Modal */}
        {tForm&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:220}}
            onClick={()=>setTForm(null)}>
            <div style={{background:"rgba(255,255,255,.95)",border:"1px solid rgba(212,175,55,.16)",borderRadius:14,padding:28,maxWidth:520,width:"100%",maxHeight:"90vh",overflow:"auto"}}
              onClick={e=>e.stopPropagation()}>
              <h3 style={{fontSize:18,color:C.gold,marginBottom:16,fontFamily:"'Inter','Montserrat',sans-serif"}}>
                {tForm.id?"编辑教法":"新建教法"}
              </h3>
              <TField label="标题 (中文)" value={tFormData.title_zh} onChange={v=>setTFormData({...tFormData,title_zh:v})} />
              <TField label="Title (EN)" value={tFormData.title_en} onChange={v=>setTFormData({...tFormData,title_en:v})} />
              <div style={{marginBottom:10}}>
                <div style={{...LS}}>类型</div>
                <select style={IS} value={tFormData.teaching_type} onChange={e=>setTFormData({...tFormData,teaching_type:e.target.value})}>
                  {TEACHING_TYPES.map(ty=><option key={ty} value={ty}>{TYPE_LABELS[ty]||ty}</option>)}
                </select>
              </div>
              <TField label="Teacher ID" value={tFormData.teacher_id} onChange={v=>setTFormData({...tFormData,teacher_id:v})} />
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <TField label="传承 Tradition" value={tFormData.tradition} onChange={v=>setTFormData({...tFormData,tradition:v})} />
                <TField label="法脉 Lineage" value={tFormData.lineage} onChange={v=>setTFormData({...tFormData,lineage:v})} />
              </div>
              <div style={{marginBottom:10}}>
                <div style={{...LS}}>描述 (中文)</div>
                <textarea value={tFormData.description_zh} onChange={e=>setTFormData({...tFormData,description_zh:e.target.value})}
                  style={{...IS,width:"100%",minHeight:60,resize:"vertical"}} />
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <TField label="Credential Key" value={tFormData.credential_key} onChange={v=>setTFormData({...tFormData,credential_key:v})} />
                <TField label="Topic Hub Slug" value={tFormData.topic_hub_slug} onChange={v=>setTFormData({...tFormData,topic_hub_slug:v})} />
              </div>
              <div style={{marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                <input type="checkbox" checked={tFormData.is_public} onChange={e=>setTFormData({...tFormData,is_public:e.target.checked})} />
                <span style={{fontSize:12,color:C.mist3}}>公开可见</span>
              </div>
              <div style={{display:"flex",gap:10,marginTop:16}}>
                <button onClick={saveTeaching} style={{...BG,flex:1}}>{tForm.id?"更新":"创建"}</button>
                <button onClick={()=>setTForm(null)} style={{...BO,flex:1}}>取消</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm */}
        {tDel&&(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:230}}>
            <div style={{background:"rgba(255,255,255,.95)",border:"1px solid rgba(212,175,55,.16)",borderRadius:14,padding:32,maxWidth:420,width:"100%"}}>
              <div style={{fontSize:18,color:C.stress,fontFamily:"'Inter','Montserrat',sans-serif",marginBottom:12}}>删除教法</div>
              <div style={{fontSize:14,color:C.mist2,marginBottom:8}}>
                确定删除 <strong>{tDel.title_zh}</strong>？
              </div>
              <div style={{fontSize:13,color:C.stress,marginBottom:20}}>
                教法将被软删除（is_active=false），不会丢失数据。关联的阶段将保留。
              </div>
              <div style={{display:"flex",gap:10}}>
                <button style={{background:"transparent",border:`1px solid ${C.stress}`,borderRadius:4,padding:"8px 16px",fontSize:13,color:C.stress,cursor:"pointer",flex:1}}
                  onClick={deleteTeaching}>删除</button>
                <button style={{...BG,flex:1}} onClick={()=>setTDel(null)}>取消</button>
              </div>
            </div>
          </div>
        )}
      </span>}
      {adminTab==="users"&&<span>

      {/* Stats Cards */}
      {stats&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:28}}>
          {[
            {label:"Total Users",value:stats.total_users,color:C.mist},
            {label:"Active",value:stats.active_users,color:C.sage},
            {label:"Reports",value:stats.total_reports,color:C.cyan},
            {label:"Revenue",value:`$${stats.total_revenue}`,color:C.gold},
            {label:"This Week",value:stats.recent_signups,color:C.fire},
            {label:"Pro",value:stats.tier_breakdown?.pro||0,color:C.gold},
          ].map((s,i)=>(
            <div key={i} style={{background:"rgba(255,255,255,.72)",border:"1px solid rgba(212,175,55,.16)",backdropFilter:"blur(12px)",borderRadius:8,padding:"14px 12px",textAlign:"center"}}>
              <div style={{fontSize:11,letterSpacing:1.5,color:C.mist4,textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
              <div style={{fontSize:22,color:s.color,fontFamily:"'Inter','Montserrat',sans-serif"}}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tier Breakdown */}
      {stats&&(
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          {Object.entries(stats.tier_breakdown||{}).map(([t,c])=>(
            <div key={t} style={{background:filterTier===t?C.goldGlow:C.panel,border:`0.5px solid ${filterTier===t?C.gold:C.border}`,borderRadius:6,padding:"6px 14px",cursor:"pointer",fontSize:13,color:filterTier===t?C.gold:C.mist3}}
              onClick={()=>{setFilterTier(filterTier===t?"":t);setTimeout(()=>loadUsers(1),100);}}>
              {t}: {c}
            </div>
          ))}
        </div>
      )}

      {/* Search + Message */}
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center"}}>
        <input style={{...IS,maxWidth:300}} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by email or name..."
          onKeyDown={e=>{if(e.key==="Enter")loadUsers(1);}}/>
        <button style={BO} onClick={()=>loadUsers(1)}>Search</button>
        <button style={{...BO,color:C.mist4}} onClick={()=>{setSearch("");setFilterTier("");setTimeout(()=>loadUsers(1),100);}}>Reset</button>
        {msg&&msg!=="Admin access required"&&<div style={{fontSize:13,color:C.sage,marginLeft:12}}>{msg}</div>}
      </div>

      {/* User Table */}
      <div style={{background:"rgba(255,255,255,.72)",border:"1px solid rgba(212,175,55,.16)",backdropFilter:"blur(12px)",borderRadius:10,overflow:"hidden"}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.6fr 0.6fr 0.8fr 1fr",gap:0,padding:"10px 16px",background:C.panel2,fontSize:11,letterSpacing:1.5,color:C.mist4,textTransform:"uppercase"}}>
          <div>Email</div><div>Name</div><div>Tier</div><div>Reports</div><div>Status</div><div>Actions</div>
        </div>
        {users.map(u=>(
          <div key={u.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.6fr 0.6fr 0.8fr 1fr",gap:0,padding:"10px 16px",borderTop:`0.5px solid ${C.border}`,fontSize:14,alignItems:"center"}}>
            <div style={{color:C.mist,overflow:"hidden",textOverflow:"ellipsis"}}>{u.email}</div>
            <div style={{color:C.mist3}}>{u.full_name||"—"}</div>
            <div><span style={{background:u.tier==="free"?"transparent":C.goldGlow,border:`0.5px solid ${u.tier==="free"?C.border:C.goldDim}`,borderRadius:4,padding:"2px 8px",fontSize:12,color:u.tier==="free"?C.mist4:C.gold}}>{u.tier}</span></div>
            <div style={{color:C.mist3}}>{u.reports_count}</div>
            <div><span style={{color:u.is_active?C.sage:C.stress,fontSize:12}}>{u.is_active?"Active":"Inactive"}</span></div>
            <div style={{display:"flex",gap:6}}>
              <button style={{background:"transparent",border:`0.5px solid ${C.cyan}`,borderRadius:4,padding:"3px 10px",fontSize:11,color:C.cyan,cursor:"pointer"}}
                onClick={()=>{setEditUser(u);setNewTier(u.tier);setReason("");}}>Tier</button>
              {u.tier!=="pro"&&<button style={{background:C.goldGlow,border:`0.5px solid ${C.gold}`,borderRadius:4,padding:"3px 8px",fontSize:11,color:C.gold,cursor:"pointer"}}
                onClick={()=>quickPro(u.id,u.email)}>日课</button>}
              <button style={{background:"transparent",border:`0.5px solid ${u.is_active?C.stress:C.sage}`,borderRadius:4,padding:"3px 8px",fontSize:11,color:u.is_active?C.stress:C.sage,cursor:"pointer"}}
                onClick={()=>toggleActive(u)}>{u.is_active?"Deactivate":"Activate"}</button>
              <button style={{background:"transparent",border:`0.5px solid ${C.stress}`,borderRadius:4,padding:"3px 8px",fontSize:11,color:C.stress,cursor:"pointer"}}
                onClick={()=>setDeleteConfirm(u)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12,fontSize:13,color:C.mist4}}>
        <div>{total} users total</div>
        <div style={{display:"flex",gap:8}}>
          {page>1&&<button style={BO} onClick={()=>loadUsers(page-1)}>← Prev</button>}
          <span style={{padding:"6px 12px",color:C.mist3}}>Page {page}</span>
          {page*15<total&&<button style={BO} onClick={()=>loadUsers(page+1)}>Next →</button>}
        </div>
      </div>

      {/* Tier Edit Modal */}
      {editUser&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200}}
          onClick={()=>setEditUser(null)}>
          <div style={{background:"rgba(255,255,255,.72)",border:"1px solid rgba(212,175,55,.16)",backdropFilter:"blur(12px)",borderRadius:14,padding:32,maxWidth:400,width:"100%"}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,color:C.gold,fontFamily:"'Inter','Montserrat',sans-serif",marginBottom:16}}>Change Tier</div>
            <div style={{fontSize:14,color:C.mist2,marginBottom:16}}>{editUser.email} — currently {editUser.tier}</div>
            <div style={{marginBottom:12}}>
              <span style={LS}>New Tier</span>
              <select style={IS} value={newTier} onChange={e=>setNewTier(e.target.value)}>
                <option value="">-- select --</option>
                {["free","pro"].map(t=><option key={t} value={t}>{t==="pro"?"Pro (澄光日课)":"Free"}</option>)}
              </select>
            </div>
            <div style={{marginBottom:16}}>
              <span style={LS}>Reason (optional)</span>
              <input style={IS} value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Complimentary upgrade"/>
            </div>
            <div style={{marginBottom:16,borderTop:`0.5px solid ${C.border}`,paddingTop:14}}>
              <span style={LS}>Reset Password</span>
              <div style={{display:"flex",gap:8}}>
                <input style={{...IS,flex:1}} type="text" value={resetPw} onChange={e=>setResetPw(e.target.value)} placeholder="New password (min 8 chars)"/>
                <button style={{...BO,color:C.stress,borderColor:C.stress,whiteSpace:"nowrap"}} onClick={()=>resetPassword(editUser.id)}>Reset</button>
              </div>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...BG,flex:1}} onClick={()=>upgradeTier(editUser.id)}>CONFIRM</button>
              <button style={{...BO,flex:1}} onClick={()=>setEditUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:210}}>
          <div style={{background:"rgba(255,255,255,.72)",border:"1px solid rgba(212,175,55,.16)",backdropFilter:"blur(12px)",borderRadius:14,padding:32,maxWidth:420,width:"100%"}}>
            <div style={{fontSize:18,color:C.stress,fontFamily:"'Inter','Montserrat',sans-serif",marginBottom:12}}>Delete User</div>
            <div style={{fontSize:14,color:C.mist2,marginBottom:8}}>
              Permanently delete <strong>{deleteConfirm.email}</strong>?
            </div>
            <div style={{fontSize:13,color:C.stress,marginBottom:20}}>
              This will also purge all {deleteConfirm.reports_count} reports and subscriptions. This action cannot be undone.
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{background:"transparent",border:`1px solid ${C.stress}`,borderRadius:4,padding:"8px 16px",fontSize:13,color:C.stress,cursor:"pointer",flex:1}}
                onClick={()=>deleteUser(deleteConfirm)}>DELETE PERMANENTLY</button>
              <button style={{...BG,flex:1}} onClick={()=>setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </span>}
    </div>
  );
}

// ── Teaching form field helper ──
function TField({label,value,onChange}){
  return (
    <div style={{marginBottom:10}}>
      <div style={{fontSize:11,color:C.mist4,marginBottom:3}}>{label}</div>
      <input value={value||""} onChange={e=>onChange(e.target.value)}
        style={{...IS,width:"100%"}} />
    </div>
  );
}
